import { get, merge } from 'lodash-es';
import { html } from 'lit-element';
import { CHART_TYPES } from '../enums';

export default (config, chartJSConfig, context) => {
  const { tooltipElement, canvas, previewMode } = context;
  const { xAxisType, series } = config;
  // eslint-disable-next-line prefer-destructuring
  const { seriesType } = series[0];

  const isMissingDrilldown =
    config?.series?.some(singleLine => !singleLine.hasDrillDown) ||
    config?.dashboardOptions?.charts?.disableDrilldowns;
  context.tooltipPosition = { x: -999, y: -999 };

  const external = chartJSContext =>
    tooltipCallback(
      chartJSContext,
      tooltipElement,
      canvas,
      previewMode,
      isMissingDrilldown,
      seriesType,
      xAxisType
    );

  const callbackObject = { options: { plugins: { tooltip: { external } } } };

  return merge(chartJSConfig, callbackObject);
};

// eslint-disable-next-line max-len
const tooltipCallback = (
  chartJSContext,
  tooltipElement,
  canvas,
  previewMode,
  isMissingDrilldown,
  seriesType,
  xAxisType
) => {
  const tooltipModel = chartJSContext.tooltip;
  if (tooltipModel.opacity === 0) {
    tooltipElement.classList.add('hide');
    return;
  }

  tooltipElement.classList.remove('hide');

  // eslint-disable-next-line max-len
  extractDataAndRenderTooltip(
    tooltipModel,
    tooltipElement,
    canvas,
    previewMode,
    isMissingDrilldown,
    seriesType,
    xAxisType,
    chartJSContext
  );
};

const getNextTick = ({ dataPoint, xAxisType }) => {
  const { chart, raw, datasetIndex } = dataPoint;
  const timeFieldAxisTypes = [
    'SINGLE_TIME_FIELD_SINGLE_NON_TIME_FIELD',
    'SINGLE_TIME_FIELD',
  ];
  const indexOfCurrentTick = chart.data.datasets[datasetIndex].data
    .map(e => e.x)
    .indexOf(raw.x);
  const nextTick =
    chart.data.datasets[datasetIndex]?.data[indexOfCurrentTick + 1]?.x;
  return {
    isTimeRange: timeFieldAxisTypes.includes(xAxisType) && nextTick,
    nextTick,
  };
};

const extractTreeMapData = ({ dataPoint }) => {
  const { dataIndex } = dataPoint;
  const mainInsightLabel = get(dataPoint, `dataset.data[${dataIndex}].g`);
  const dimensionObject = dataPoint.dataset.tree.find(
    item => item.x === mainInsightLabel
  );
  const { otherBuckets, yFormatted: tooltipMainInsight } = dimensionObject;
  return {
    tooltipMainInsight,
    mainInsightLabel,
    tooltipPositionText: null,
    otherBuckets,
  };
};

const extractSankeyData = ({ dataPoint }) => {
  const { yFormatted: tooltipMainInsight, to } = dataPoint?.raw;
  const mainInsightLabel = to.includes('_others') ? 'Other' : to;
  return {
    tooltipMainInsight,
    mainInsightLabel,
  };
};

const extractDoughnutData = ({ dataPoint, chartJSContext }) => {
  const dataArr = chartJSContext.chart.data.datasets[0].data;
  const sum = dataArr.reduce(
    (accu, current) => (accu += current?.value || 0),
    0
  );
  const ratio = ((dataPoint.raw.value / sum) * 100).toFixed(2);
  return {
    tooltipMainInsight: `${dataPoint.raw.formattedValue} (${ratio}%)`,
    mainInsightLabel: dataPoint.label,
  };
};
const precentFrom = (nominator, demoninator) => {
  let precentage = (nominator / demoninator) * 100;
  precentage = precentage % 1
    ? precentage.toFixed(2)
    : Math.round(precentage);
  return precentage;
};

const extractFunnelData = ({ dataPoint }) => {
  const { datasetIndex } = dataPoint;
  const getPointValue = index => dataPoint.chart.config.data.datasets[index].data[0].y;
  let prevValue; let precentageFromPrevValue = 0;
  const firstValue = getPointValue(0);
  const hoveredValue = dataPoint.parsed.y;
  const isFirstItem = datasetIndex === 0;

  if (!isFirstItem) {
    prevValue = getPointValue(datasetIndex - 1);
    precentageFromPrevValue = precentFrom(hoveredValue, prevValue);
  }
  const precentageFromFirstValue = precentFrom(hoveredValue, firstValue);

  return {
    tooltipMainInsight: get(dataPoint, 'raw.yFormatted'),
    mainInsightLabel: get(dataPoint, 'raw.aggregationLabel'),
    tooltipPositionText: `
      ${
        !isFirstItem ? `
          <div>${precentageFromFirstValue}% from the first step</div>
          <div>${precentageFromPrevValue}% from the previous step</div>
        `
        : ``
}      
    `,
    note: get(dataPoint, 'raw.note'),
    cardPosition: 'top',
  };
};

/**
 * This func render this ↓ data to the tooltip popup box, and gets data about the next tick.
 * @param {object} dataPoint - data
 * @param {string} xAxisType - represent the x axis type for rendering by that agg
 * @param {string} seriesType - represnt the series type of the general chart. 'LINE','BAR'...
 * @return {{tooltipMainInsight:string,mainInsightLabel:string,tooltipPositionText:string,note:string}}
 * {
    tooltipMainInsight: renders the precentage of the value by two digits after the .,
    mainInsightLabel: render the aggregation kind of the chart,
    tooltipPositionText: render the label of the category / segment, if this is horizontal the prop will render the raw.y value.
  }
 */
const extractDefaultData = ({ dataPoint, xAxisType, seriesType }) => {
  const { isTimeRange, nextTick } = getNextTick({ dataPoint, xAxisType });
  const isHorizontalBar = seriesType === CHART_TYPES.HORIZONTAL_BAR;
  return {
    tooltipMainInsight: get(dataPoint, 'raw.yFormatted'),
    mainInsightLabel: get(dataPoint, 'raw.aggregationLabel'),
    tooltipPositionText: isHorizontalBar
      ? `${get(dataPoint, 'raw.y')}`
      : `${get(dataPoint, 'raw.x')} ${isTimeRange ? `- ${nextTick}` : ''}`,
    note: get(dataPoint, 'raw.note'),
  };
};

const extractTooltipData = (
  seriesType,
  { dataPoint, xAxisType },
  chartJSContext
) => {
  /* This is a hack!
    On confetti we declare uppercase seriesType and in the generateBasicChartJSConfig builder in FcChart.js we use it to set up the config type
    In doughnut-builder.js we just set it up in the DEFAULT_CONFIG.
    While in the tooltip-config-builder.js we use seriesType and not config type. so we get uppercase.
    Yet all the stories in the fc-dashboard.stories.js are lowercase altho they should be uppercase.
    This is A BIG MESS!!! take it in consideration!
  */
  const type = seriesType.toLowerCase();

  switch (type) {
    case CHART_TYPES.SANKEY:
      return extractSankeyData({ dataPoint });
    case CHART_TYPES.TREEMAP:
      return extractTreeMapData({ dataPoint });
    case CHART_TYPES.DOUGHNUT:
      return extractDoughnutData({ dataPoint, chartJSContext });
    case CHART_TYPES.FUNNEL:
      return extractFunnelData({ dataPoint });
    default:
      return extractDefaultData({ dataPoint, xAxisType, seriesType });
  }
};

// eslint-disable-next-line max-len
const extractDataAndRenderTooltip = (
  tooltipModel,
  tooltipElement,
  canvas,
  previewMode,
  isMissingDrilldown,
  seriesType,
  xAxisType,
  chartJSContext
) => {
  const dataPoint = get(tooltipModel, 'dataPoints[0]');
  const { offsetLeft: positionX, offsetTop: positionY } = canvas;

  const tooltipX = tooltipModel.caretX;
  const dataset = chartJSContext.chart.data.datasets[dataPoint.datasetIndex];
  const isLastInSeries = dataPoint.dataIndex === (dataset.data || []).length - 1;
  const isFirstInSeries = dataPoint.dataIndex === 0;
  const isBarOrLineChart = [CHART_TYPES.BAR, CHART_TYPES.LINE].includes(seriesType.toLowerCase());

  if (isBarOrLineChart) {
    if (isLastInSeries) {
      tooltipElement.classList.add('align-right');
      tooltipElement.classList.remove('align-left');
    } else if (isFirstInSeries) {
      tooltipElement.classList.add('align-left');
      tooltipElement.classList.remove('align-right');
    } else {
      tooltipElement.classList.remove('align-right', 'align-left');
    }
  } else {
    tooltipElement.classList.remove('align-right', 'align-left');
  }

  tooltipElement.style.left = `${tooltipX + positionX}px`;
  tooltipElement.style.top = `${tooltipModel.caretY + positionY}px`;
  tooltipElement.style.setProperty(
    '--fc-chart-tooltip-color',
    dataPoint.element?.options?.colorFrom ||
      dataPoint.element?.options?.backgroundColor
  );

  const extractedData = {
    previewMode,
    isMissingDrilldown,
    ...extractTooltipData(seriesType, { dataPoint, xAxisType }, chartJSContext),
  };

  tooltipElement.innerHTML = renderTooltip(extractedData);
};

/* eslint-disable */
const renderTooltip = ({
  previewMode,
  tooltipMainInsight,
  mainInsightLabel,
  tooltipPositionText,
  tooltipAdditionalInsights = [],
  isMissingDrilldown,
  otherBuckets,
  note,
  cardPosition,
}) => {
  return `
    <div class="pin"></div>
    <div class="card" data-card-position=${cardPosition}>
      <div class="tooltip-header ${
        isMissingDrilldown ? "missing-drilldown" : ""
      }">
        <span class="main-insight">${tooltipMainInsight} ${mainInsightLabel}</span>
        ${
          tooltipPositionText
            ? `<span class="tooltip-position">${tooltipPositionText}</span>`
            : ""
        }
        ${
          note
            ? `<span class="tooltip-note"><fc-icon-svg class="tooltip-note-icon"></fc-icon-svg>${note}</span>`
            : ""
        }
      </div>
      ${tooltipAdditionalInsights
        .map(
          (tooltipAdditionalInsight) => html`
            <span class="additional-insight main-insight"
              >${tooltipAdditionalInsight}</span
            >
          `
        )
        .join("")}
      ${
        (otherBuckets || []).length > 0
          ? renderOtherBuckets({ otherBuckets })
          : ""
      }
      ${!previewMode && !isMissingDrilldown ? renderDrillDown() : ""}
    </div>`;
};

const renderDrillDown = () => {
  return `
    <div class="drill-down">
      <forter-icon icon="info-empty" size="xsmall"></forter-icon>
      Click to explore further
    </div>`;
};

const renderOtherBuckets = ({ otherBuckets }) => {
  return `
    <div class="others-container">
      ${otherBuckets
        .map((bucket) => {
          const { label, formattedValue } = bucket;
          return `<span class="additional-insight main-insight">${label} ${formattedValue}</span>`;
        })
        .join("")}
    </div>
  `;
};
